import React, { useState } from 'react';
import SkyViewer from './SkyViewer.jsx';
import GuessPanel from './GuessPanel.jsx';
import { TIERS, ROUNDS_PER_GAME, MAX_ROUND_SCORE } from './scoring.mjs';
import { eventDateLabel, wikiUrl } from './events.mjs';
import { useSettings } from './SettingsMenu.jsx';
import {
  formatDistance,
  formatUtcClock,
  formatDate,
  formatMonthDay,
  formatCoords,
  moonLabel,
  localSolarTime
} from './format.mjs';

export default function RoundView({
  round,
  rings,
  texture,
  moonTexture,
  roundIndex,
  runningTotal,
  result,
  onSubmitGuess,
  onNext
}) {
  const tier = TIERS[round.tier];
  const settings = useSettings();
  const [guess, setGuess] = useState(null);
  // Collapsed to start, so a round opens on the sky rather than on the map. The
  // reveal re-opens it, since the result is the thing you want to see next.
  const [panelOpen, setPanelOpen] = useState(false);

  const canSubmit = Boolean(guess);
  const solarClock = guess ? localSolarTime(round.date, guess.lon) : null;

  const submit = () => {
    if (!canSubmit || result) return;
    onSubmitGuess({ guess });
    setPanelOpen(true);
  };

  return (
    <div className="sg-round">
      <div className="sg-round-bar">
        <span className="sg-round-count">
          Round {roundIndex + 1} <span className="sg-muted">of {ROUNDS_PER_GAME}</span>
        </span>
        <span className="sg-given">
          <span className="sg-given-label">Time</span>
          <strong>{formatUtcClock(round.date)}</strong>
          <span className="sg-muted">UTC</span>
        </span>
        <span className="sg-given">
          <span className="sg-given-label">Date</span>
          {/*
            An On This Night round withholds the year until the guess is in: the
            month and day are today's and are what the navigation needs, but the
            year would name the event and give away the place.
          */}
          <strong>
            {round.event && !result ? formatMonthDay(round.date) : formatDate(round.date)}
          </strong>
        </span>
        <span className="sg-score-so-far">{runningTotal.toLocaleString()} pts</span>
      </div>

      <div className="sg-stage">
        <SkyViewer
          round={round}
          texture={texture}
          moonTexture={moonTexture}
          initialAz={round.initialAz}
          revealed={Boolean(result)}
          assisted={tier.assisted}
        />

        <GuessPanel
          title={result ? 'Result' : 'Your guess'}
          open={panelOpen}
          onToggle={() => setPanelOpen((open) => !open)}
          mapProps={{
            rings,
            pairs: [{ guess, actual: result ? { lat: round.lat, lon: round.lon } : null }],
            onGuess: result ? null : setGuess
          }}
        >
          {!result && (
            <div className="sg-panel-body">
              {guess ? (
                <div className="sg-guess-readout">
                  <p className="sg-guess-coords">{formatCoords(guess.lat, guess.lon)}</p>
                  <p className="sg-guess-time">
                    <span className="sg-muted">Local time:</span> <strong>{solarClock}</strong>
                  </p>
                </div>
              ) : (
                /* The verb the reader's device actually offers; see `.sg-hint-coarse`. */
                <p className="sg-guess-prompt">
                  <span className="sg-hint-fine">Click</span>
                  <span className="sg-hint-coarse">Tap</span> the map to drop a pin.
                </p>
              )}


              <button type="button" className="sg-primary" disabled={!canSubmit} onClick={submit}>
                {canSubmit ? 'Lock in guess' : 'Drop a pin first'}
              </button>
            </div>
          )}

          {result && (
            <div className="sg-panel-body">
              {round.event && (
                <div className="sg-event">
                  <p className="sg-event-lead">The sky as seen by</p>
                  <p className="sg-event-name">{round.event.seenBy}</p>
                  <p className="sg-event-where">
                    {round.event.place} &middot; {eventDateLabel(round.event)},{' '}
                    {localSolarTime(round.date, round.event.lon)}
                  </p>
                  <p className="sg-event-note">{round.event.note}</p>
                  {/*
                    The reveal is the one moment the game tells you something
                    instead of asking, and the next question is always "what was
                    that?" — so the card answers it. New tab deliberately: the
                    round is not over, and there are two more to play.
                  */}
                  <a
                    className="sg-event-link"
                    href={wikiUrl(round.event)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {round.event.wiki} on Wikipedia →
                  </a>
                </div>
              )}
              <p className="sg-result-score">
                <strong>{result.roundScore.toLocaleString()}</strong>{' '}
                <span className="sg-muted">/ {MAX_ROUND_SCORE.toLocaleString()}</span>
              </p>
              <dl className="sg-result-rows">
                <div>
                  <dt>Missed by</dt>
                  <dd>{formatDistance(result.distanceKm, settings.units)}</dd>
                </div>
                <div>
                  <dt>Your guess</dt>
                  <dd>{guess ? formatCoords(guess.lat, guess.lon) : '—'}</dd>
                </div>
                <div>
                  <dt>Answer</dt>
                  <dd>{formatCoords(round.lat, round.lon)}</dd>
                </div>
                <div>
                  <dt>Local time</dt>
                  <dd>{localSolarTime(round.date, round.lon)}</dd>
                </div>
                <div>
                  <dt>Moon</dt>
                  <dd>{moonLabel(round.moon)}</dd>
                </div>
              </dl>
              <button type="button" className="sg-primary" onClick={onNext}>
                {roundIndex + 1 === ROUNDS_PER_GAME ? 'See final score' : 'Next round'}
              </button>
            </div>
          )}
        </GuessPanel>
      </div>
    </div>
  );
}


